import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatTimeAgo } from '@/lib/utils';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { CreateJobDialog } from '@/components/dashboard/CreateJobDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Plus, Activity, CheckCircle, XCircle, Brain, Clock,
  TrendingUp, ArrowRight, Server
} from 'lucide-react';

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [statsData, jobsData] = await Promise.all([
        api.getStats(),
        api.listJobs({ limit: 5 }),
      ]);
      setStats(statsData);
      setRecentJobs(jobsData.jobs || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Dashboard
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Autonomous IP Delisting Agent Overview</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-new-job">
          <Plus className="w-4 h-4 mr-2" /> New Delisting Job
        </Button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <KPICard icon={Activity} label="Active Jobs" value={stats?.active_jobs || 0} color="text-primary" />
        <KPICard icon={Server} label="Total Jobs" value={stats?.total_jobs || 0} color="text-foreground" />
        <KPICard icon={CheckCircle} label="Succeeded" value={stats?.succeeded || 0} color="text-emerald-400" />
        <KPICard icon={XCircle} label="Failed" value={stats?.failed || 0} color="text-red-400" />
        <KPICard icon={TrendingUp} label="Success Rate" value={`${stats?.success_rate || 0}%`} color="text-amber-400" />
        <KPICard icon={Brain} label="KB Blacklists" value={stats?.kb_blacklists || 0} color="text-purple-400" />
      </div>

      {/* Recent Jobs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Recent Jobs
            </CardTitle>
            <Link to="/jobs" className="text-xs text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentJobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No jobs yet. Start your first delisting job.
            </div>
          ) : (
            <div className="space-y-2">
              {recentJobs.map(job => (
                <Link
                  key={job.job_id}
                  to={`/jobs/${job.job_id}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-secondary/50 transition-colors"
                  data-testid={`job-row-${job.job_id}`}
                >
                  <div className="flex items-center gap-4">
                    <StatusBadge status={job.status} pulse />
                    <div>
                      <span className="font-mono text-sm">{job.ip}</span>
                      <span className="text-muted-foreground mx-2">&rarr;</span>
                      <span className="text-sm text-muted-foreground">{job.blacklist_name || job.blacklist}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatTimeAgo(job.created_at)}
                    </span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateJobDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => loadData()}
      />
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-4 h-4 ${color}`} />
          <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{label}</span>
        </div>
        <div className={`text-2xl font-semibold kpi-number ${color}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
